import type {
  CommunicationDeliveryMode,
  CommunicationPurpose,
  CommunicationSuppressionReason,
  CustomerCommunicationStatus,
} from './crm';
import type {
  CountChange,
  MoneyByCurrency,
  MoneyChange,
  OpticalAttention,
  ProductTypeQuantity,
  ReportDimension,
  ReportFilterCode,
  ReportGranularity,
  ReportMeta,
  ReportMetricType,
  ReportMetricUnit,
  ReportPanel,
  ReportPreset,
  TrendBucket,
} from './reporting';
import type { PaymentMethod } from './sales';

/**
 * Smart Report Builder (Phase 6.8).
 *
 * A guided, deterministic way to ask the reporting engine one question. There is no language model
 * and no free text anywhere in it: the browser composes a `SmartReportRequest` from choices the
 * server offered, and the server validates it, builds a `ValidatedReportQueryPlan` with the same
 * `ReportPlanBuilder` the tabs use, runs the same area engine and projects the requested figure out
 * of that engine's own result. A future natural-language layer would have to produce the same
 * request and pass the same validation; it would add no execution path of its own.
 */

/** The areas the builder can open. The Overview is a combination of these, never an area of its own. */
export const SMART_REPORT_AREAS = [
  'SALES',
  'INVENTORY',
  'FINANCE',
  'CUSTOMERS',
  'OPTICAL',
  'CRM',
] as const;
export type SmartReportArea = (typeof SMART_REPORT_AREAS)[number];

/** Over time, at the granularity the engine chooses for the period. The engine picks day, week or month, never the caller. */
export const SMART_TREND = 'TREND' as const;
export type SmartDimension = ReportDimension | typeof SMART_TREND;

export const SmartVisualization = {
  KPI: 'KPI',
  LINE: 'LINE',
  BAR: 'BAR',
  STACKED_BAR: 'STACKED_BAR',
  TABLE: 'TABLE',
} as const;
export type SmartVisualization = (typeof SmartVisualization)[keyof typeof SmartVisualization];
export const SMART_VISUALIZATIONS = Object.values(SmartVisualization);

export const SmartComparison = { NONE: 'NONE', PREVIOUS: 'PREVIOUS' } as const;
export type SmartComparison = (typeof SmartComparison)[keyof typeof SmartComparison];

export const SmartSort = { DESC: 'DESC', ASC: 'ASC' } as const;
export type SmartSort = (typeof SmartSort)[keyof typeof SmartSort];

/** The ranking sizes offered; the engines never return more than 50 rows for any breakdown. */
export const SMART_TOP_N = [5, 10, 20, 50] as const;
export const SMART_MAX_LIMIT = 50;
/** The area engines break a figure down one way at a time, so a request names at most one dimension. */
export const SMART_MAX_DIMENSIONS = 1;
export const SMART_MAX_METRICS = 1;

/**
 * The filters a request may carry, each mapped to the one field of the matching tab's own query that
 * carries it. Every id is resolved and checked by `ReportPlanBuilder` exactly as the tab's would be.
 * `OPTICAL_ATTENTION` chooses which attention list is counted; it is not a query filter.
 */
export const SMART_FILTER_FIELDS = {
  BRANCH: 'branchId',
  OPERATOR: 'operatorId',
  PRODUCT: 'productId',
  VARIANT: 'variantId',
  CATEGORY: 'categoryId',
  BRAND: 'brandId',
  CUSTOMER: 'customerId',
  SUPPLIER: 'supplierId',
  EXPENSE_CATEGORY: 'expenseCategoryId',
  CURRENCY: 'currencyCode',
  PAYMENT_METHOD: 'paymentMethod',
  LAB: 'labId',
  COMM_PURPOSE: 'purpose',
  COMM_STATUS: 'status',
  DELIVERY_MODE: 'deliveryMode',
  COMM_TEMPLATE: 'templateId',
  SUPPRESSION_REASON: 'suppressionReason',
  FAILURE_REASON: 'failureCode',
  OPTICAL_ATTENTION: 'attention',
} as const satisfies Partial<Record<ReportFilterCode, string>>;
export type SmartFilterCode = keyof typeof SMART_FILTER_FIELDS;
export const SMART_FILTER_CODES = Object.keys(SMART_FILTER_FIELDS) as SmartFilterCode[];

export interface SmartReportFilters {
  branchId?: string;
  operatorId?: string;
  productId?: string;
  variantId?: string;
  categoryId?: string;
  brandId?: string;
  customerId?: string;
  supplierId?: string;
  expenseCategoryId?: string;
  currencyCode?: string;
  paymentMethod?: PaymentMethod;
  labId?: string;
  purpose?: CommunicationPurpose;
  status?: CustomerCommunicationStatus;
  deliveryMode?: CommunicationDeliveryMode;
  templateId?: string;
  suppressionReason?: CommunicationSuppressionReason;
  failureCode?: string;
  attention?: OpticalAttention;
}

/** A period exactly as the tabs take it: a preset, or a custom range of calendar dates resolved on the server. */
export interface SmartReportPeriod {
  preset: ReportPreset;
  from?: string;
  to?: string;
}

/**
 * What the browser sends. Codes and approved filter fields only: never a tenant, an actor, a plan,
 * a SQL fragment or a metric the server did not offer. Unknown properties are refused outright.
 */
export interface SmartReportRequest {
  area: SmartReportArea;
  /** Exactly one in this release. */
  metricCodes: string[];
  /** At most one. */
  dimensions: SmartDimension[];
  /** Null for a figure that is only ever "as of now". */
  period: SmartReportPeriod | null;
  comparison: SmartComparison;
  filters: SmartReportFilters;
  sort: SmartSort;
  /** Top N, only where the chosen breakdown can be ranked. */
  limit: number | null;
  visualization: SmartVisualization;
}

// ---------------------------------------------------------------------------
// The builder's catalogue: what this caller may choose, derived on the server
// ---------------------------------------------------------------------------

/** One choosable metric: a catalogue metric, sometimes narrowed by fixed filters into a named variant. */
export interface SmartMetricOption {
  code: string;
  /** Null for the plain metric; a code for a named variant such as READY_PAST. */
  variant: string | null;
  area: SmartReportArea;
  panel: ReportPanel;
  unit: ReportMetricUnit;
  type: ReportMetricType;
  perCurrency: boolean;
  /** True when the figure exists only as of now, so no period is asked for. */
  asOfNow: boolean;
  comparable: boolean;
  /** Filters a variant always carries; the builder shows them and never lets them be removed. */
  fixedFilters: SmartReportFilters;
  dimensions: SmartDimension[];
  /** The breakdowns whose rows may be ranked and cut to a top N. */
  rankable: SmartDimension[];
  /** The breakdowns whose value splits into same-unit parts, so a stacked bar is truthful. */
  stackable: SmartDimension[];
  /** False when a chart would add unlike things (quantities of different product types) or plot a yes/no state: a table only. */
  chartable: boolean;
  filters: SmartFilterCode[];
}

export interface SmartAreaOption {
  area: SmartReportArea;
  metrics: SmartMetricOption[];
}

export interface SmartBuilderCatalogue {
  areas: SmartAreaOption[];
  /** The caller's accessible branches: "all branches" means these and only these. */
  branches: { id: string; name: string }[];
  baseCurrencyCode: string;
  maxRangeDays: number;
  /** Pre-built requests this caller may run, already validated against their authority. */
  quickReports: SmartSuggestion[];
}

/** One choice for an entity or code filter. Codes are translated by the web; names come from the tenant's own records. */
export interface SmartFilterOption {
  value: string;
  label: string | null;
  labelAr: string | null;
}

export interface SmartFilterOptions {
  filter: SmartFilterCode;
  options: SmartFilterOption[];
}

// ---------------------------------------------------------------------------
// The result
// ---------------------------------------------------------------------------

/** One value, typed by what it measures. Money stays per currency; quantities stay per product type. */
export type SmartValue =
  | { kind: 'MONEY'; amounts: MoneyByCurrency }
  | { kind: 'AVERAGE_MONEY'; amounts: { currencyCode: string; amountMinor: number | null }[] }
  | { kind: 'COUNT'; value: number }
  | { kind: 'QUANTITY_BY_TYPE'; quantities: ProductTypeQuantity[] }
  | { kind: 'DURATION'; seconds: number | null }
  | { kind: 'MODE_COUNTS'; total: number; automated: number; manual: number };

export interface SmartFigure {
  current: SmartValue;
  /** Only with a comparison, and only for figures the engine compares. */
  previous: SmartValue | null;
  moneyChange: MoneyChange[] | null;
  countChange: CountChange | null;
}

/** A breakdown row. `id` is an entity id or null (unattributed); `code` is an enumerated value the web translates. */
export interface SmartRow {
  id: string | null;
  code: string | null;
  label: string | null;
  labelAr: string | null;
  value: SmartValue;
}

export interface SmartBreakdown {
  dimension: SmartDimension;
  rows: SmartRow[];
  /** Rows beyond the requested top N were cut. */
  truncated: boolean;
  /** Money rows are ranked within this one currency, never across currencies. */
  rankCurrency: string | null;
}

export interface SmartTrendSeries {
  /** A currency code for money, AUTOMATED/MANUAL for communications, COUNT otherwise. */
  key: string;
  values: (number | null)[];
}

export interface SmartTrend {
  granularity: ReportGranularity;
  buckets: TrendBucket[];
  series: SmartTrendSeries[];
}

export const SmartInsightCode = {
  CHANGE_UP: 'CHANGE_UP',
  CHANGE_DOWN: 'CHANGE_DOWN',
  NO_CHANGE: 'NO_CHANGE',
  TOP_CONTRIBUTOR: 'TOP_CONTRIBUTOR',
  MIXED_CURRENCY: 'MIXED_CURRENCY',
  PARTIAL_ESTIMATE: 'PARTIAL_ESTIMATE',
  DEAD_STOCK: 'DEAD_STOCK',
  UNKNOWN_MOVEMENT_HISTORY: 'UNKNOWN_MOVEMENT_HISTORY',
  OVERDUE_COUNT: 'OVERDUE_COUNT',
  ATTENTION_COUNT: 'ATTENTION_COUNT',
  TURNAROUND_COVERAGE: 'TURNAROUND_COVERAGE',
  INCOMPLETE_HISTORY: 'INCOMPLETE_HISTORY',
  FAILURE_COUNT: 'FAILURE_COUNT',
  SUPPRESSION_COUNT: 'SUPPRESSION_COUNT',
  WHATSAPP_UNAVAILABLE: 'WHATSAPP_UNAVAILABLE',
  NO_REGISTRATION_BRANCH: 'NO_REGISTRATION_BRANCH',
  TRUNCATED: 'TRUNCATED',
  NO_ACTIVITY: 'NO_ACTIVITY',
} as const;
export type SmartInsightCode = (typeof SmartInsightCode)[keyof typeof SmartInsightCode];

export type SmartInsightSeverity = 'INFO' | 'POSITIVE' | 'WARNING' | 'CRITICAL';

/** A parameter of an insight sentence. Every number is copied from the result, never computed from text. */
export type SmartInsightParam =
  | { kind: 'MONEY'; currencyCode: string; amountMinor: number }
  | { kind: 'COUNT'; value: number }
  | { kind: 'PERCENT'; basisPoints: number }
  | { kind: 'DURATION'; seconds: number }
  | { kind: 'ENTITY'; label: string | null; labelAr: string | null }
  | { kind: 'CODE'; dimension: SmartDimension; code: string };

/** A rule-based observation. Its words come from a localized template keyed by `code`; nothing is free text. */
export interface SmartInsight {
  code: SmartInsightCode;
  severity: SmartInsightSeverity;
  metricCode: string;
  params: Record<string, SmartInsightParam>;
}

/** A pre-built next question: a full request the server already checked this caller may run. */
export interface SmartSuggestion {
  key: string;
  request: SmartReportRequest;
}

export interface SmartReportResult {
  /** The request as the server validated and normalised it. */
  request: SmartReportRequest;
  meta: ReportMeta;
  metric: SmartMetricOption;
  figure: SmartFigure;
  breakdown: SmartBreakdown | null;
  trend: SmartTrend | null;
  /** The names of the entities the filters resolved to, in the tenant's own words. */
  filterLabels: { filter: SmartFilterCode; label: string | null; labelAr: string | null }[];
  insights: SmartInsight[];
  /** The matching tab, with the tab's own query parameters, for the full report and its records. */
  tab: { path: string; params: Record<string, string> };
  suggestions: SmartSuggestion[];
}

/**
 * The visualizations a figure may be drawn as, decided by rule: a single figure is a KPI, a figure
 * over time a line, a figure by category a bar. Stacking is offered only where the parts share a
 * unit (automated and manual messages); currencies and product types are never stacked, and a
 * figure that cannot be drawn on one axis truthfully is a table.
 */
export function smartVisualizations(
  dimension: SmartDimension | null,
  stackable: boolean,
  chartable = true,
): SmartVisualization[] {
  if (dimension === null) return [SmartVisualization.KPI];
  if (!chartable) return [SmartVisualization.TABLE];
  const shapes: SmartVisualization[] =
    dimension === SMART_TREND
      ? [SmartVisualization.LINE, SmartVisualization.BAR]
      : [SmartVisualization.BAR];
  if (stackable) shapes.push(SmartVisualization.STACKED_BAR);
  shapes.push(SmartVisualization.TABLE);
  return shapes;
}
